import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, CardHeader, EmptyState, Pagination, SearchInput, Skeleton } from "@/hq/HQPrimitives";

const STAGES = ["NEW","RESEARCHING","SCANNED","QUALIFIED","CAMPAIGN_READY","CONTACTED","ENGAGED","OPPORTUNITY","QUOTE","WON","LOST","DO_NOT_CONTACT"];

function AddProspectModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    business_name: "", email: "", website_url: "", contact_name: "",
    phone: "", industry: "", country: "", notes: "",
    contact_basis: "legitimate_interest",
  });
  const [saving, setSaving] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await hqApi.post("/sales/prospects", form);
      toast.success("Prospect added.");
      onCreated?.(data.prospect);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to add.");
    } finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="add-prospect-modal">
      <form onSubmit={submit} className="w-full max-w-lg bg-white rounded-2xl border border-[#EAEAEA] p-6 space-y-3">
        <h3 className="font-black text-lg">Add prospect</h3>
        {["business_name","contact_name","email","website_url","phone","industry","country"].map((k) => (
          <label key={k} className="block">
            <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">{k.replace(/_/g," ")}</span>
            <input
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              data-testid={`prospect-input-${k.replace(/_/g,"-")}`}
              className="mt-1 w-full px-3 py-2 border border-[#EAEAEA] rounded-lg text-[13px]"
            />
          </label>
        ))}
        <label className="block">
          <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Contact basis</span>
          <select
            value={form.contact_basis}
            onChange={(e) => setForm({ ...form, contact_basis: e.target.value })}
            data-testid="prospect-input-contact-basis"
            className="mt-1 w-full px-3 py-2 border border-[#EAEAEA] rounded-lg text-[13px]"
          >
            <option value="legitimate_interest">Legitimate interest</option>
            <option value="consent">Consent</option>
            <option value="existing_customer">Existing customer</option>
            <option value="unspecified">Unspecified</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Notes</span>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="prospect-input-notes"
            className="mt-1 w-full px-3 py-2 border border-[#EAEAEA] rounded-lg text-[13px] min-h-[70px]"
          />
        </label>
        <p className="text-[11px] text-[#8A8A8A]">Provide a website URL or an email. Both help dedupe against existing customers.</p>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[12px] font-bold tracking-[0.12em] uppercase" data-testid="prospect-add-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-full bg-[#FF6A00] text-black text-[12px] font-black tracking-[0.12em] uppercase disabled:opacity-50" data-testid="prospect-add-submit">
            {saving ? "Saving…" : "Add"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function HQSalesProspects() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await hqApi.get("/sales/prospects", {
        params: { page, page_size: 25, q: q || undefined, stage: stage || undefined },
      });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, stage]);

  return (
    <div className="space-y-4">
      <Card>
        <div className="px-6 py-4 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Sales Engine</div>
            <h2 className="mt-1 font-black text-lg">Prospects</h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAdd(true)}
              className="px-4 py-2 rounded-full bg-[#FF6A00] text-black text-[12px] font-black tracking-[0.12em] uppercase"
              data-testid="prospects-add-button"
            >Add prospect</button>
          </div>
        </div>
        <div className="px-6 pb-4 flex flex-col sm:flex-row gap-2 sm:items-center">
          <SearchInput value={q} onChange={setQ} testId="prospects-search" placeholder="Search name, domain or email…" />
          <button onClick={() => { setPage(1); load(); }} className="px-4 py-2.5 rounded-full border border-[#EAEAEA] text-[12px] font-bold tracking-[0.12em] uppercase" data-testid="prospects-search-button">Search</button>
          <select
            value={stage}
            onChange={(e) => { setStage(e.target.value); setPage(1); }}
            data-testid="prospects-stage-filter"
            className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px]"
          >
            <option value="">All stages</option>
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="px-6 pb-6">
          {loading ? (
            <div className="space-y-2"><Skeleton h={40} /><Skeleton h={40} /><Skeleton h={40} /></div>
          ) : !data.items?.length ? (
            <EmptyState title="No prospects" hint="Add a prospect above to get started." testId="prospects-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A]">
                    <th className="py-2">Business</th>
                    <th className="py-2">Domain</th>
                    <th className="py-2">Stage</th>
                    <th className="py-2">Compliance</th>
                    <th className="py-2">Score</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((p) => (
                    <tr key={p.id} className="border-t border-[#EAEAEA]" data-testid={`prospect-row-${p.id}`}>
                      <td className="py-3">
                        <div className="font-semibold">{p.business_name || p.contact_name || "—"}</div>
                        <div className="text-[11px] text-[#8A8A8A]">
                          {p.contact_name || ""}
                          {p.is_demo && <span className="ml-2 px-1.5 py-0.5 rounded bg-[#0A0A0A] text-white text-[9px] tracking-[0.2em] uppercase">DEMO</span>}
                        </div>
                      </td>
                      <td className="py-3 text-[12px]">{p.normalized_domain || p.normalized_email || "—"}</td>
                      <td className="py-3"><span className="px-2 py-1 rounded-full bg-[#FFF7EE] border border-[#FCE1C7] text-[10px] font-black tracking-[0.16em]">{p.stage}</span></td>
                      <td className="py-3 text-[11px] text-[#8A8A8A]">
                        {p.opt_out && "opt-out "}{p.suppressed && "suppressed "}{p.do_not_contact && "DNC "}
                        {!p.opt_out && !p.suppressed && !p.do_not_contact && "OK"}
                      </td>
                      <td className="py-3 font-black">{p.orange_opportunity_score?.score ?? "—"}
                        {p.orange_opportunity_score?.band && (
                          <span className="ml-1 text-[10px] tracking-[0.2em] font-bold text-[#FF6A00]">{p.orange_opportunity_score.band}</span>
                        )}
                      </td>
                      <td className="py-3">
                        <Link to={`/hq/sales/prospects/${p.id}`} className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#FF6A00]" data-testid={`prospect-open-${p.id}`}>Open →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="prospects-page" />
        </div>
      </Card>
      {showAdd && <AddProspectModal onClose={() => setShowAdd(false)} onCreated={() => load()} />}
    </div>
  );
}
